import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";

import * as _ from "lodash"
import {am4Settings} from "@/mixins/am4charts/am4Settings"

const COLOR_LEFT = am4core.color("#3d81f7")
const COLOR_RIGHT = am4core.color("#1c4019")

export const SeriesLegacy = class {
    constructor(chart, id) {
        this.create(chart, id)
    }

    /**
     * Add Series to Charts list of series w/out showing the series.
     * - While Adding series ensure that the series is not duplicated
     */
    setupSeries(shouldShow = true) {
        if (!this.isSetupValid()) {
            return
        }

        if (shouldShow) {
            this.show()
        }
        else {
            this.hide()
        }

        let seriesList = this.seriesChart.series
        if (seriesList.contains(this.am4Series)) {
            return
        }

        if (this.am4Series.isDisposed()) {
            this.create(this.seriesChart, this.id, this.axisPosition)
        }

        this.seriesChart.series.push(this.am4Series)
        this.setupBullets()
        this.setupTooltip()
    }

    getSeries() {
        return this.am4Series
    }

    getYAxis() {
        return this.seriesChart.yAxes.getIndex(this.axisPosition)
    }

    setAxisLeft() {
        this.axisPosition = am4Settings.AXIS_LEFT
        this.am4Series.yAxis = this.seriesChart.yAxes.getIndex(0)
        this.color = COLOR_LEFT
        this.am4Series.stroke = this.color
        this.am4Series.fill = this.color

    }

    setAxisRight() {
        this.axisPosition = am4Settings.AXIS_RIGHT
        this.am4Series.yAxis = this.seriesChart.yAxes.getIndex(1)
        this.color = COLOR_RIGHT
        this.am4Series.stroke = this.color
        this.am4Series.fill = this.color

    }

    /**
     * Remove the series from the chart list and code it to be hidden
     */
    removeSeries() {
        if (!this.isSetupValid()) {
            return
        }

        this.hide()
        this.reset()
        this.seriesChart.series.removeValue(this.am4Series)
    }

    hide() {
        if (!this.isSetupValid()) {
            return
        }

        this.am4Series.visible = false
        _.each(this.am4ThresholdAxisRanges, threshold => {
            threshold.hide()
        })
        this.visible = false
    }

    show() {
        if (!this.isSetupValid()) {
            return
        }

        this.am4Series.visible = true
        _.each(this.am4ThresholdAxisRanges, threshold => {
            threshold.show()
        })
        this.visible = true
    }

    reset() {
        this.am4Series.bullets.clear()
        this.am4Series.tooltip.label.adapter.remove('textOutput')
        this.am4ThresholdAxisRanges = {}
        this.getYAxis().axisRanges.clear()
    }

    setupBullets() {
        if (!this.isSetupValid()) {
            return
        }

        let bullets = this.am4Series.bullets.push(new am4core.Circle())
        bullets.radius = 3

        bullets.adapter.add('disabled', param => {
            return this.seriesChart.data.length === 0
        })
    }

    setupTooltip() {
        if (!this.isSetupValid()) {
            return
        }

        this.am4Series.tooltipText = `{${this.id}_tooltip}`
        this.am4Series.tooltip.label.adapter.add("textOutput",
                (text, target, key) => {
                    if (target.dataItem && target.dataItem.dataContext) {
                        return target.dataItem.dataContext[`${this.id}_tooltip`]
                    }
                    return text;
                }
        );

        this.am4Series.tooltip.pointerOrientation = "horizontal"
        this.am4Series.tooltip.background.cornerRadius = 20
        this.am4Series.tooltip.background.fillOpacity = 1
        this.am4Series.tooltip.background.propertyFields.fill = 'backgroundColor'
        this.am4Series.tooltip.background.propertyFields.stroke = 'backgroundColor'
        this.am4Series.tooltip.label.padding(12,12,12,12)
    }

    setupThresholds() {
        let alarmInfo = this.getAlarmInfo()
        let alarmValues = {}
        this.setupThresholdRange(alarmInfo.alarm, am4Settings.COLOR_ALARM, alarmValues)
        this.setupThresholdRange(alarmInfo.caution, am4Settings.COLOR_CAUTION, alarmValues)

        return alarmValues
    }

    setupThresholdRange(values, color, alarmValues) {
        let valueAxis = this.getYAxis()

        Object.keys(values).forEach(key => {
            let item = values[key]
            if (item === null) {
                return
            }
            let range = valueAxis.axisRanges.create();
            range.value = item;
            range.grid.stroke = color
            range.grid.strokeWidth = 2;
            range.grid.strokeOpacity = 1;
            if (this.axisPosition === am4Settings.AXIS_RIGHT) {
                range.grid.strokeDasharray = '5,4'
            }

            this.am4ThresholdAxisRanges.push(range)

            if (key === 'high_alarm'){
                alarmValues.highAlarmValue = item;
            } else if (key === 'low_alarm'){
                alarmValues.lowAlarmValue = item;
            }
            else if (key === 'high_caution'){
                alarmValues.highCautionValue = item;
            } else if (key === 'low_caution'){
                alarmValues.lowCautionValue = item;
            }
        })
    }

    updateFromSeriesData(seriesData) {
        Object.keys(seriesData).forEach(key => {
            this[key] = seriesData[key]
        })
    }

    updateInterval(start, end) {
        // TODO. Figure out a proper copy of JS Date.
    }

    isSetupValid() {
        return !(
                _.isNil(this.seriesChart) ||
                _.isNil(this.am4Series)
        );
    }

    setAlarmInfo(alarmInfo) {
        this.alarmInfo = alarmInfo
    }

    getAlarmInfo() {
        if (_.isNil(this.alarmInfo)) {
            return {
                alarm: {},
                caution: {},
            }
        }

        return this.alarmInfo
    }

    create(chart, id, axisPosition = am4Settings.AXIS_LEFT) {
        this.requestNumber = 0
        this.min = 100000
        this.max = -100000
        this.numData = 0
        this.lastUpdateTime = null
        this.alarmInfo = null
        this.start = null
        this.end = null
        this.scalingProfile = null
        this.am4ThresholdAxisRanges = []

        this.visible = false
        this.seriesChart = chart
        this.am4Series = new am4charts.LineSeries()
        this.color = axisPosition === axisPosition.AXIS_LEFT
                ? COLOR_LEFT
                : COLOR_RIGHT

        this.id = id
        this.axisPosition = axisPosition

        if (!_.isNil(this.seriesChart)) {
            this.am4Series.yAxis = this.seriesChart.yAxes.getIndex(axisPosition)
        }
        this.am4Series.dataFields.dateX = 'date'
        this.am4Series.dataFields.valueY = `${id}`
        this.am4Series.strokeWidth = 2
        this.am4Series.connect = true
        this.am4Series.stroke = this.color
        this.am4Series.fill = this.color
    }

    dispose() {
        if (!_.isNil(this.am4Series)) {
            this.am4Series.dispose()
            this.am4Series = null
        }
    }
}